/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import { type ReactNode } from 'react';
import { Link } from 'gatsby';

import {
  appendParams,
  getAttributionParams,
  TRACKING_LABELS
} from '../../utils';

declare global {
  interface Window {
    analytics: any;
    gtag: any;
    rudderanalytics: any;
  }
}

interface LinkWithQueryProps {
  activeClassName?: string;
  children: ReactNode;
  className?: string;
  isExternal?: boolean;
  noreferrer?: boolean;
  partiallyActive?: boolean;
  tabIndex?: number;
  to: string;
  trackingLabel?: string;
}

export function logTracking(data: {
  href?: string;
  label?: string;
  source?: string;
}) {
  const { label, href, source } = data;

  // Rudderstack
  try {
    if (typeof window !== 'undefined' && window.rudderanalytics) {
      if (label) {
        if (label === TRACKING_LABELS.VideoPlayed) {
          window.rudderanalytics.track(TRACKING_LABELS.VideoPlayed, {
            src: source,
            url: location.href
          });
        } else {
          window.rudderanalytics.track('CTA Button Clicked', {
            button: label,
            href
          });
        }
      } else if (href) {
        window.rudderanalytics?.track('Outbound Link', {
          href: href
        });
      }
    }
  } catch {
    console.error('Error logging to Rudderstack');
  }

  //GA
  try {
    if (typeof window !== 'undefined' && window.gtag) {
      if (label) {
        window.gtag('event', 'click', {
          event_category: 'button',
          event_label: label
        });
      } else if (href) {
        window.gtag('event', 'click', {
          event_category: 'Outbound Link',
          event_label: href
        });
      }
    }
  } catch {
    console.error('Error logging to GA');
  }
}

export const LinkWithQuery: React.ComponentType<
  LinkWithQueryProps & { DANGEROUS_AS_DIV?: boolean }
> = React.forwardRef(
  (
    {
      children,
      to,
      className,
      isExternal = false,
      noreferrer = true,
      trackingLabel,
      DANGEROUS_AS_DIV,
      tabIndex,
      ...otherProps
    },
    ref
  ) => {
    // retrieve any stored attribution params from session storage
    const attributionParams = getAttributionParams();

    // retrive attribution params if /signup
    const link =
      to.includes('app.hex.tech') &&
      attributionParams !== null &&
      attributionParams !== undefined
        ? appendParams(to, attributionParams)
        : to;

    if (DANGEROUS_AS_DIV) {
      return (
        // eslint-disable-next-line react/jsx-no-target-blank
        <div
          //@ts-expect-error not typing this
          ref={ref}
          className={className}
          onClick={e => {
            // using a div here because of children links and valid dom nesting.
            // But don't want to open two links at the same time if a child
            // link is clicked. We should change the design of this component
            // to not be a clickable thing inside a clickable thing, but for now
            // we have to check manually to not accidently fire both.
            let targetIsSubLink = false;
            if (e.target instanceof Element) {
              const nearestAnchorAncestor = e.target.closest('a');
              targetIsSubLink = e.currentTarget.contains(nearestAnchorAncestor);
            }
            if (targetIsSubLink) return;

            logTracking({ href: link, label: trackingLabel });
            window.open(
              link,
              '_blank',
              noreferrer ? 'noopener noreferrer' : 'noopener'
            );
          }}>
          {children}
        </div>
      );
    }

    if (isExternal) {
      return (
        // eslint-disable-next-line react/jsx-no-target-blank
        <a
          //@ts-expect-error not typing this
          ref={ref}
          className={className}
          href={link}
          rel={noreferrer ? 'noopener noreferrer' : 'noopener'}
          tabIndex={tabIndex}
          target="_blank"
          onClick={() => logTracking({ href: link, label: trackingLabel })}>
          {children}
        </a>
      );
    }

    return (
      <Link
        //@ts-expect-error not typing this
        ref={ref}
        className={className}
        to={link}
        {...otherProps}
        tabIndex={tabIndex}
        onClick={() => logTracking({ href: link, label: trackingLabel })}>
        {children}
      </Link>
    );
  }
);
